import React, { useState } from 'react';

import http from 'utilities/http';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useMediaSet } from 'use-media-set';

import ResumeRevLogo from 'assets/illustrations/resume-rev-logo.svg';
import TheStudyLogo from 'assets/illustrations/the-study-white.svg';
import DTCLogo from 'assets/illustrations/dtc-logo.svg';
import DTCTalentCloud from 'assets/illustrations/dtc-talent-cloud.svg';
import ResumeModal from 'components/talent/dashboard/Resume/ResumeModal';
import { Box } from 'components/ui/Box';
import Button from 'components/ui/buttons/Button';
import Bell from 'components/ui/icons/Bell';
import Caret from 'components/ui/icons/Caret';
import Flex from 'components/ui/Flex';
import FlexItem from 'components/ui/FlexItem';
import ProgressBar from 'components/ui/ProgressBar';
import { colors, mediaSetQueries } from 'styleGuide';

export const Collapsible = styled(Flex)`
  transition: all 0.5s ease-in, padding, margin 0.05s ease-in;
  overflow: hidden;
  padding: 0 40px;
  height: auto;
  &.expanded {
    max-height: 500px;
    margin-top: 24px;
  }
  &.collapsed,
  &.hid {
    max-height: 0px;
    transition: all 0.5s ease-out, padding, margin 0.05s ease-out 0.45s;
  }
`;
const StyledBox = styled(Box)`
  max-width: 100%;
  margin-left: 1rem;
  margin-right: 1rem;
  .clickable {
    cursor: pointer;
  }
  .flex_row > .flex_column {
    flex: 1 1 auto;
  }
  .flex_row > .flex_column > * {
    max-width: 100%;
  }
`;
const StyledCaretButton = styled(Button)`
  align-self: center;
  margin: -12.5px 0;
`;
const StyledImg = styled.img`
  align-self: flex-start;
  max-height: 100%;
  max-width: 255px;
  width: auto;
`;

const ActionCard = ({ action, onChange, user }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const actionDetail = action.action;

  const images = {
    resume_rev: ResumeRevLogo,
    the_study: TheStudyLogo,
    dtclogo: DTCLogo,
    dtctalentcloud: DTCTalentCloud,
  };
  const mediaStates = useMediaSet(mediaSetQueries);
  const onSmallScreen = mediaStates.has('mobile') || mediaStates.has('tablet');

  const getComponent = (id, props) => {
    if (id === 'resume_rev') {
      return <ResumeModal {...props} />;
    }
  };

  const getSafeHtml = (c, key, user) => {
    const val = c.value;
    const content = val.content || val;
    const variable = val.variable ? user[val.variable] : null;
    const iHtml = variable ? content.replace('{variable}', variable) : content;
    const innerHtml = {
      __html: iHtml,
    };
    return <div dangerouslySetInnerHTML={innerHtml} key={key} />;
  };

  const goToRoute = route => {
    const isExternal = /(http|www|\.com)/.test(route);
    if (isExternal) {
      window.open(route, '_blank');
    } else {
      window.location.assign(route);
    }
  };

  const handleButtonClick = route => {
    if (actionDetail.action_type === 'button') {
      updateAction('is_completed').then(() => {
        goToRoute(route);
      });
    } else {
      goToRoute(route);
    }
  };

  const actionCardVariableRegexp = /\$\{[a-z].+?\}/g;
  const getUserActionDataVariables = text => {
    let matches = text.match(actionCardVariableRegexp);
    if (!matches || !matches.length) {
      return text;
    }
    let templateVariables = matches.reduce((result, match) => {
      result.push(match.replace(/\$|{|}/g, ''));
      return result;
    }, []);

    let variableMap = templateVariables.reduce((mapping, variable) => {
      mapping[variable] = action.data[variable];
      return mapping;
    }, {});

    return interpolateTemplateVariables(text, variableMap);
  };

  const interpolateTemplateVariables = (string, params) => {
    let names = Object.keys(params);
    let vals = Object.values(params);
    return new Function(...names, `return \`${string}\`;`)(...vals);
  };

  const updateAction = prop => {
    const updatedAction = Object.assign(action, {
      [prop]: true,
    });
    return http
      .patch(`/api/v1/moms/actions/${action.id}`, {
        credentials: 'same-origin',
        referrer: 'no-referrer',
        body: JSON.stringify({ user_action: updatedAction }),
      })
      .then(res => onChange(res))
      .catch(err => console.error(err));
  };

  const getActionBlock = (c, onSmallScreen) => {
    const key = c.key ? c.key : c.type;
    switch (c.type) {
      case 'button':
        if (typeof c.value.route !== 'undefined') {
          c.value.route = getUserActionDataVariables(c.value.route);
        }
        return (
          <Flex direction="row" gap={24} justifyContent="flex-end" key={key}>
            <Button
              id="btn-dismiss-action"
              inline
              link
              onClick={() => updateAction('is_dismissed')}
            >
              Dismiss
            </Button>
            {c.value.route && (
              <Button id={key} onClick={() => handleButtonClick(c.value.route)}>
                {c.value.label}
              </Button>
            )}
            {c.value.modal && (
              <>
                <Button
                  id={key}
                  onClick={ev => {
                    ev.preventDefault();
                    setModalOpen(true);
                  }}
                >
                  {c.value.label}
                </Button>
                {modalOpen &&
                  getComponent(c.value.modal, {
                    handleClose: ev => {
                      ev.preventDefault();
                      setModalOpen(false);
                      updateAction('is_completed');
                    },
                  })}
              </>
            )}
          </Flex>
        );
      case 'column':
      case 'row':
        return (
          <Flex
            alignItems={c.type === 'column' || onSmallScreen ? 'stretch' : null}
            className={'flex_' + (onSmallScreen ? 'column' : c.type)}
            direction={onSmallScreen ? 'column' : c.type}
            gap={onSmallScreen ? 12 : 24}
            justifyContent={c.type === 'column' ? 'space-between' : null}
            key={key}
          >
            {c.components.map(comp => getActionBlock(comp))}
          </Flex>
        );
      case 'html':
        if (typeof c.value === 'string') {
          c.value = getUserActionDataVariables(c.value);
        }
        return getSafeHtml(c, key, user);
      case 'image':
        return (
          <StyledImg
            alt={c.value.label}
            key={key}
            src={images[c.value.variable]}
          />
        );
      case 'progress':
        return (
          <ProgressBar
            alwaysStar
            barHeightOnly
            key={key}
            percentage={user[c.value.variable]}
            withCircle
          />
        );
      case 'text':
        if (typeof c.value === 'string') {
          c.value = getUserActionDataVariables(c.value);
        }
        return (
          <FlexItem
            className={key === 'subtitle' ? 'font-bolder' : ''}
            key={key}
          >
            {c.value}
          </FlexItem>
        );
      default:
        break;
    }
  };

  // Handle Action Card Title
  if (
    action.data &&
    ['has_presented_jobs?', 'has_thankable_bids?'].includes(
      actionDetail.audience_attribute,
    )
  ) {
    actionDetail.title = getUserActionDataVariables(actionDetail.title);
  }

  return (
    <StyledBox
      backgroundColor={colors.green.pale}
      className={isExpanded ? 'expanded' : 'collapsed'}
      padding="30px 20px"
      size="md"
    >
      <Flex alignItems="stretch" direction="column" justifyContent="flex-start">
        <Flex
          alignItems={onSmallScreen ? 'flex-start' : 'center'}
          className="clickable"
          gap={16}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <Bell color={colors.blue.primary} size={25} />
          <FlexItem className="font-heavy" grow={1} shrink={1}>
            {actionDetail.title}
          </FlexItem>
          <StyledCaretButton
            aria-controls={`action-content-${action.action_id}`}
            aria-expanded={isExpanded}
            iconOnly
            id={`toggle-button-${action.action_id}`}
            onClick={() => null}
          >
            <Caret
              color={colors.blue.primary}
              direction={isExpanded ? 'up' : 'down'}
              size={20}
            />
          </StyledCaretButton>
        </Flex>
        <Collapsible
          alignItems="stretch"
          className={isExpanded ? 'expanded' : 'collapsed'}
          direction="column"
          id={`action-content-${action.action_id}`}
          justifyContent="flex-start"
        >
          <Flex
            alignItems="stretch"
            direction="column"
            gap={30}
            justifyContent="flex-start"
          >
            {actionDetail.properties.components.map(c =>
              getActionBlock(c, onSmallScreen),
            )}
          </Flex>
        </Collapsible>
      </Flex>
    </StyledBox>
  );
};

ActionCard.propTypes = {
  action: PropTypes.any,
  onChange: PropTypes.func,
  user: PropTypes.any,
};

export default ActionCard;
